import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { formatEatDate, formatEatTime } from '@wodi/domain';
import type { RetentionScheduleView } from '@wodi/shared';
import {
  Button,
  Cell,
  Column,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  Row,
  Section,
  Table,
  TableBody,
  TableHeader,
  Tag,
  TextField,
} from '@wodi/ui';
import { useState } from 'react';

import { privacyKeys, retentionQuery, saveRetentionRule } from '../api/privacy.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { TableScroll } from '../layout/screen.tsx';
import { SettingsPage } from '../routes/settings-nav.tsx';
import { toasts } from '../toasts.ts';
import { errorText } from './labels-settings-page.tsx';
import styles from './privacy.module.css';

// Settings > Data retention (docs/plans/golive-privacy-tooling.md §4.3): how long system data
// is kept before the daily job purges it. Clinical records are never deleted. Every period is
// provisional: TODO(legal-verify) (plan P6).

type Rule = RetentionScheduleView['rules'][number];

function RuleDialog({ rule, onClose }: { rule: Rule; onClose: () => void }) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const [days, setDays] = useState(String(rule.keepDays));
  const value = Number(days);
  const valid = Number.isInteger(value) && value >= rule.minDays && value <= rule.maxDays;
  const mutation = useMutation({
    mutationFn: () => saveRetentionRule(rule.category, value),
    onSuccess: (view) => {
      queryClient.setQueryData(privacyKeys.retention, view);
      toasts.show({ title: 'Retention period saved' });
      onClose();
    },
  });
  return (
    <Dialog
      title="Retention period"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <p className={styles.prose}>{rule.description}.</p>
      <TextField
        label="Keep for"
        unit="days"
        inputMode="numeric"
        width="sm"
        value={days}
        onChange={setDays}
        description={`From ${String(rule.minDays)} to ${String(rule.maxDays)} days. The default is ${String(rule.defaultDays)}.`}
        {...(valid || days === ''
          ? {}
          : {
              errorMessage: `Whole days from ${String(rule.minDays)} to ${String(rule.maxDays)}.`,
            })}
      />
      {mutation.error === null ? null : (
        <Notice tone="error">{errorText(mutation.error, 'The period was not saved.')}</Notice>
      )}
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isDisabled={!valid || !online}
          isPending={mutation.isPending}
          pendingLabel="Saving…"
          onPress={() => {
            mutation.mutate();
          }}
        >
          Save period
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function LastRun({ view }: { view: RetentionScheduleView }) {
  if (view.lastRun === null)
    return <p className={styles.meta}>The daily purge hasn&apos;t run yet. It runs at 03:30.</p>;
  const at = new Date(view.lastRun.at);
  const total = Object.values(view.lastRun.counts).reduce((sum, n) => sum + n, 0);
  return (
    <p className={styles.meta}>
      {`Last purge ${formatEatDate(at)} at ${formatEatTime(at)}: ${String(total)} ${total === 1 ? 'record' : 'records'} purged. It runs daily at 03:30.`}
    </p>
  );
}

function Schedule({ view }: { view: RetentionScheduleView }) {
  const online = useOnline();
  const [editing, setEditing] = useState<Rule | null>(null);
  return (
    <>
      <Section
        title="System data purged"
        description="Provisional periods, waiting for legal review. Changing one takes effect at the next daily purge."
      >
        <LastRun view={view} />
        <TableScroll>
          <Table aria-label="Retention periods" flat>
            <TableHeader>
              <Column isRowHeader>What</Column>
              <Column>Kept for</Column>
              <Column>Last purge</Column>
              <Column>Action</Column>
            </TableHeader>
            <TableBody items={view.rules.map((r) => ({ ...r, id: r.category }))}>
              {(r) => (
                <Row id={r.id}>
                  <Cell>{r.description}</Cell>
                  <Cell>
                    <span className={styles.numbers}>{`${String(r.keepDays)} days`}</span>{' '}
                    {r.isDefault ? <Tag state="pending">Provisional</Tag> : null}
                  </Cell>
                  <Cell>
                    <span className={styles.numbers}>
                      {view.lastRun === null ? '—' : String(view.lastRun.counts[r.category] ?? 0)}
                    </span>
                  </Cell>
                  <Cell>
                    <Button
                      dense
                      variant="quiet"
                      isDisabled={!online}
                      onPress={() => {
                        setEditing(r);
                      }}
                    >
                      Change
                    </Button>
                  </Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      <Section title="Kept by rule" description="The purge never touches these.">
        <TableScroll>
          <Table aria-label="Records kept by rule" flat>
            <TableHeader>
              <Column isRowHeader>What</Column>
              <Column>Kept</Column>
            </TableHeader>
            <TableBody items={view.fixed.map((f) => ({ ...f, id: f.record }))}>
              {(f) => (
                <Row id={f.id}>
                  <Cell>{f.record}</Cell>
                  <Cell>{f.kept}</Cell>
                </Row>
              )}
            </TableBody>
          </Table>
        </TableScroll>
      </Section>
      {editing === null ? null : (
        <RuleDialog
          rule={editing}
          onClose={() => {
            setEditing(null);
          }}
        />
      )}
    </>
  );
}

export function RetentionSettingsPage() {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const allowed = session !== null && can(session, 'privacy.retention.manage');
  const schedule = useQuery({ ...retentionQuery, enabled: allowed });
  if (!allowed)
    return (
      <SettingsPage title="Data retention">
        <Notice tone="info">You don&apos;t have permission to see the retention schedule.</Notice>
      </SettingsPage>
    );
  return (
    <SettingsPage title="Data retention">
      {schedule.isPending ? <LoadingBlock label="Loading the retention schedule" rows={4} /> : null}
      {schedule.isError ? (
        <Notice
          tone="error"
          action={<Button onPress={() => void schedule.refetch()}>Try again</Button>}
        >
          {errorText(schedule.error, 'The retention schedule could not be loaded. Try again.')}
        </Notice>
      ) : null}
      {schedule.isSuccess ? <Schedule view={schedule.data} /> : null}
    </SettingsPage>
  );
}
